import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ENVIRONMENT_TOKEN, Environment } from '@shared/models/environment.model';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class AuthService {
  /** observable that will emit a boolean flag specifying if the user is authenticated or not */
  public isAuthenticated$ = new BehaviorSubject<boolean>(false);

  /** Current value of isAuthenticated flag */
  public get isAuthenticated(): boolean {
    return this.isAuthenticated$.getValue();
  }

  constructor(
    @Inject(ENVIRONMENT_TOKEN) private env: Environment,
    private jwtHelper: JwtHelperService
  ) {}

  public checkIfAuthenticated(token: string): void {
    if (this.isTokenInvalid(token)) {
      this.setUnauthenticated();
      return;
    }

    const tokenPayload = this.jwtHelper.decodeToken(token);
    if (!this.env.jwtTokenIssuer.includes(tokenPayload['iss'])) {
      this.setUnauthenticated();
      return;
    }

    const workOrderIds = this.getWorkOrderIds(tokenPayload);
    if (!workOrderIds.length) {
      this.setUnauthenticated();
      return;
    }

    window.sessionStorage.setItem('access_token', token);
    window.sessionStorage.setItem('zendesk_enabled', String(tokenPayload['ZendeskEnabled'] ?? false));
    window.sessionStorage.setItem('workorder_id', workOrderIds[0]);
    window.sessionStorage.setItem('workorder_ids', JSON.stringify(workOrderIds));
    window.sessionStorage.setItem('workorder_claim_type', Array.isArray(tokenPayload['WorkOrders']) ? 'multi' : 'single');
    this.isAuthenticated$.next(true);
  }

  private isTokenInvalid(token: string): boolean {
    return !token || this.jwtHelper.isTokenExpired(token);
  }

  private setUnauthenticated(): void {
    this.isAuthenticated$.next(false);
  }

  private getWorkOrderIds(tokenPayload: Record<string, unknown>): string[] {
    if (typeof tokenPayload['WorkOrderId'] === 'string') {
      return [tokenPayload['WorkOrderId']];
    }

    return Array.isArray(tokenPayload['WorkOrders'])
      ? tokenPayload['WorkOrders'].filter((workOrderId): workOrderId is string => typeof workOrderId === 'string')
      : [];
  }
}
